// libraries.
import { useEffect } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { electricPurple500, monserratSemiBold } from "@habitech/shared";

// interfaces.
import { ExceptionRule } from "../rulesData";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";

// styles.
import { StyledRulesModalForm } from "../styles";

interface Props {
  exception: ExceptionRule | null;
  onClose: () => void;
}

type FormValues = Omit<ExceptionRule, "id">;

export const EditExceptionModal = ({ exception, onClose }: Props) => {
  const { saveException, isSaving } = useRulesSlice();
  const show = exception !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (exception) {
      reset({
        name: exception.name,
        description: exception.description,
        field: exception.field,
        triggerValue: exception.triggerValue,
      });
    }
  }, [exception, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!exception) return;
    await saveException({ ...exception, ...data });
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header
        closeButton
        style={{ borderBottom: `2px solid ${electricPurple500}` }}
      >
        <Modal.Title
          style={{
            fontFamily: monserratSemiBold,
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Editar excepción
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <StyledRulesModalForm className="d-flex flex-column gap-3">
            <Form.Group>
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Nombre de la excepción"
                {...register("name", { required: "Requerido" })}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                placeholder="Describe cuándo aplica esta excepción"
                {...register("description", { required: "Requerido" })}
                isInvalid={!!errors.description}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Campo técnico</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Ej: Apuesta, Categoría Grid"
                {...register("field", { required: "Requerido" })}
                isInvalid={!!errors.field}
              />
              <Form.Control.Feedback type="invalid">
                {errors.field?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Valor de activación</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                placeholder="Ej: 1"
                {...register("triggerValue", {
                  required: "Requerido",
                  valueAsNumber: true,
                })}
                isInvalid={!!errors.triggerValue}
              />
              <Form.Control.Feedback type="invalid">
                {errors.triggerValue?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </StyledRulesModalForm>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
          <Button
            className="btn-habi-white"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button type="submit" className="btn-habi" disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" animation="border" />
            ) : (
              "Guardar cambios"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
